<script setup lang="ts">
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import type {FileReference, FileVariant, ObjectId} from "~/composables/charon";
import watermark from "assets/images/watermark.png"
import {useToast} from "vue-toastification";

const config = useRuntimeConfig()
const { charon } = useCharon(true)
const route = useRoute()
const router = useRouter()
const toast = useToast()
const { t, locales, locale } = useI18n()

const emits = defineEmits<{
  (e: 'click:select', value: ObjectId[]): void
  (e: 'click:close'): void
}>()

const props = defineProps<{
  select: 'single' | 'multi' | 'none'
  filter?: Record<string, string>
}>()

// FILE FETCH

const query = ref("")
const filter = computed(() => ({
  ...(props.filter || {}),
  "description[fuzzyi]": query.value,
}))
const pageSize = 12
const pageIndex = ref(0)
const pageCount = computed(() => Math.ceil((referenceCount.value || 0) / pageSize))
const hasNextPage = computed(() => pageIndex.value < pageCount.value)

const {data: referenceCount, refresh: refreshCount} = useAsyncData(
  () => charon<number>(`file-references/count`, {
    params: {
      ...filter.value,
    }
  }),
  {watch: [filter], server: false}
)

const references = ref<FileReference[]>([])
const loading = ref(true)
const loadingState = ref(0) // used to discard invalid requests
const loadNextPage = async () => {
  loading.value = true
  loadingState.value = (loadingState.value + 1) % 10000
  const state = loadingState.value
  return charon<FileReference[]>(`file-references`, {
    params: {
      ...filter.value,
      sort: ['createDate[dec]'],
      page: pageIndex.value,
      size: pageSize,
    }
  })
    .then(res => {
      // check that query has not changed
      if (state != loadingState.value) {
        return
      }
      references.value.push(...res)
      pageIndex.value += 1
    })
    .finally(() => {
      loading.value = false
    })
}

const refreshReferences = () => {
  references.value = []
  pageIndex.value = 0
  refreshCount()
  // load first page
  loadNextPage()
}

watch(filter, () => {
  refreshReferences()
})

onMounted(() => {
  // load first page
  refreshReferences()
})

// FILE SELECT

const selected = ref<ObjectId[]>([])
const select = (reference: FileReference) => {
  if (props.select === 'single' && selected.value.length > 0) {
    selected.value.pop()
  }

  const index = selected.value.indexOf(reference.id!!)
  if (index !== -1) {
    selected.value.splice(index, 1)
  } else {
    selected.value.push(reference.id!!)
  }
}

const confirm = () => {
  emits('click:select', selected.value)
  emits('click:close')
  // reset state
  form.value = null
  refreshReferences()
}

const cancel = () => {
  emits('click:close')
  // reset state
  form.value = null
  refreshReferences()
}

// FILE EDIT

const form = ref<{
  before: string
  reference: FileReference
  file: File | null
} | null>(null)

const edit = (reference: FileReference) => {
  form.value = {
    before: JSON.stringify(reference),
    reference: {
      ...reference,
      editData: reference.editData ?? {
        useWatermark: null,
        useOverlayText: null,
        overlayText: null,
      }
    },
    file: null,
  }
}

const changed = computed(() => form.value && form.value?.before !== JSON.stringify(form.value?.reference))

const canvas = ref<HTMLCanvasElement | null>(null)
watchEffect(() => {
  // register values to watch for
  const useWatermark = form.value?.reference.editData?.useWatermark ?? false
  const useOverlayText = form.value?.reference.editData?.useOverlayText ?? false
  const overlayText = form.value?.reference.editData?.overlayText ?? null

  // load original to be edited again
  if (form.value === null || !form.value.reference.original?.mediaType?.startsWith("image")) {
    return
  }

  if (!canvas.value) {
    return
  }

  const ctx = canvas.value!!.getContext('2d')!!

  const imgPromise = new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.src = `${config.public.charonUrl}/file-references/file/${form.value!!.reference.id}?original=true`
  })

  const markPromise = new Promise((resolve) => {
    const mark = new Image()
    mark.crossOrigin = "anonymous"
    mark.onload = () => resolve(mark)
    mark.src = watermark
  })

  Promise
    .all([imgPromise, markPromise])
    .then((res) => {
      const img: HTMLImageElement = res[0] as never
      const mark: HTMLImageElement = res[1] as never

      // insert background and scale
      canvas.value!!.width = img.width
      canvas.value!!.height = img.height
      ctx.drawImage(img, 0,0);

      // insert watermark
      if (useWatermark) {
        const aspect = mark.height / mark.width
        ctx.globalAlpha = 0.6;
        ctx.drawImage(mark, img.width - 100 - 30,img.height - 100 * aspect - 30, 100, 100 * aspect);
        ctx.globalAlpha = 1.0;
      }

      // insert overlay image
      if (useOverlayText) {
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = 'white'
        ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
        ctx.shadowOffsetX = 5
        ctx.shadowOffsetY = 5
        ctx.shadowBlur = 25
        ctx.font = '160px Bebas Neue'
        ctx.fillText(overlayText?.toUpperCase() ?? '', img.width / 2, (img.height / 2) + 8)
      }
    })
})

const getCanvasFile = (): Promise<File> => {
  return new Promise<File>((resolve, reject) => {
    canvas.value!!.toBlob((blob) => {
      if (!blob) {
        reject()
      }
      const filename = `${form.value!!.reference.id}_edited.webp`
      const file = new File([blob!!], filename, { type: "image/webp" })
      resolve(file)
    }, 'image/webp')
  })
}

// FILE UPLOAD

const dropActive = ref(false)
function onDrop(e: DragEvent) {
  const files = e.dataTransfer?.files
  if (!files || files.length !== 1) {
    toast.warning(t('jc.component.fileViewer.notification.singularUploadError'))
    return
  }

  // if editing open, replace file, else upload as new
  const file = files.item(0)!!
  if (form.value != null) {
    replaceFile(file)
  } else {
    uploadFile(file)
  }
}

const uploading = ref(false)
const onFileChange = (e: Event) => {
  const files = (e.target as HTMLInputElement).files
  if (files?.length === 0) {
    return
  }
  // notify that only one file can be uploaded at once
  if (files?.length !== 1) {
    toast.warning(t('jc.component.fileViewer.notification.singularUploadError'))
    return
  }

  // if editing open, replace file, else upload as new
  const file = files.item(0)!!
  if (form.value != null) {
    replaceFile(file)
  } else {
    uploadFile(file)
  }
}

const uploadFile = (file: File) => {
  uploading.value = true
  const formData = new FormData()
  // TODO is this correct?
  formData.append("reference", JSON.stringify({
    id: null,
    createDate: null,
    locales: {},
    original: {
      createDate: null,
      mediaType: null,
      extension: null,
    } satisfies FileVariant,
    edited: null,
    editData: null,
  } satisfies FileReference))
  formData.append("file", file)
  charon<FileReference>(
    `/file-references/file`,
    {
      method: 'PUT',
      body: formData
    }
  )
    .then(() => {
      toast.success(t('jc.component.fileViewer.notification.uploadComplete'))
      form.value = null
      refreshReferences()
    })
    .catch(reason => {
      toast.success(t('jc.component.fileViewer.notification.uploadFailed', { reason: reason }))
    })
    .finally(() => {
      uploading.value = false
    })
}

const replacing = ref(false)
const replaceFile = (file: File) => {
  if (form.value == null) {
    return
  }
  replacing.value = true

  const formData = new FormData()
  formData.append("reference", JSON.stringify(form.value.reference!!))
  formData.append("file", file)
  charon<FileReference>(
    `/file-references/file`,
    {
      method: 'PATCH',
      body: formData
    }
  )
    .then(() => {
      toast.success(t('jc.component.fileViewer.notification.reuploadComplete'))
      form.value = { ...form.value!! }
      refreshReferences()
    })
    .catch(reason => {
      toast.success(t('jc.component.fileViewer.notification.reuploadFailed', { reason: reason }))
    })
    .finally(() => {
      replacing.value = false
    })
}

// FILE SAVE

const saving = ref(false)
const save = async () => {
  if (!form.value) {
    return
  }
  saving.value = true

  const original = form.value!!.file

  const formData = new FormData()
  formData.append("reference", JSON.stringify(form.value!!.reference))
  if (changed.value) formData.append("edited", await getCanvasFile())
  if (original !== null) formData.append("original", original)

  await charon<FileReference>(
    `/file-references/file`,
    {
      method: 'PATCH',
      body: formData
    }
  )
    .then(() => {
      toast.success(t('jc.component.fileViewer.notification.saveComplete'))
      form.value = null
      refreshReferences()
    })
    .catch(reason => {
      toast.success(t('jc.component.fileViewer.notification.saveFailed', { reason: reason }))
    })
    .finally(() => {
      saving.value = false
    })
}

// FILE DELETE

const deleting = ref(false)
const deleteFile = () => {
  if (form.value === null || !window.confirm(t('jc.component.fileViewer.prompt.confirmDeletion'))) {
    return
  }

  deleting.value = true

  charon<FileReference>(
    `/file-references/${form.value!!.reference.id!!}`,
    {
      method: 'DELETE',
    }
  )
    .then(() => {
      toast.success(t('jc.component.fileViewer.notification.deleteComplete'))
      form.value = null
      refreshReferences()
    })
    .catch(reason => {
      toast.success(t('jc.component.fileViewer.notification.deleteFailed', { reason: reason }))
    })
    .finally(() => {
      deleting.value = false
    })
}

// locales

const selectableLocales = computed(() => Object.keys(form.value?.reference?.locales ?? {}))
const selectedLocale = ref(
  // try select current locale
  selectableLocales.value.find(l => l == locale.value)
  // try select any language
  ?? selectableLocales.value.find(_ => true)
  ?? null
)

watch(form, () => {
  selectedLocale.value = (
    // try select current locale
    selectableLocales.value.find(l => l == locale.value)
    // try select any language
    ?? selectableLocales.value.find(_ => true)
    ?? null
  )
})

function addLocale(locale: string) {
  if (!form.value) return

  form.value.reference.locales = {
    ...form.value.reference.locales,
    [locale]: {
      description: "",
    }
  }

  selectedLocale.value = locale
}

function removeLocale(locale: string) {
  if (!form.value) return

  // remove from body
  form.value.reference.locales = Object.fromEntries(
    Object.entries(form.value.reference.locales)
      .filter(([l, _]) => l !== locale)
  )

  if (selectedLocale.value == locale) {
    selectedLocale.value = selectableLocales.value.length > 0 ? selectableLocales.value[0] : null
  }
}
</script>

<template>
  <Overlay lock>
    <div
      class="fixed inset-0 z-50 h-screen overflow-y-auto
        bg-white flex flex-col"
      :data-active="dropActive"
      @drop.prevent.stop="onDrop"
      @dragenter.prevent="dropActive = true"
      @dragover.prevent="dropActive = true"
      @dragleave.prevent="dropActive = false"
    >
      <Container class="my-20">
        <!-- title bar -->
        <div
          class="flex flex-row gap-10"
        >
          <button
            v-if="form !== null"
            class="text-[1.2em] hover:text-secondary-highlighted active:text-secondary-selected
              flex justify-center items-center h-[2em] w-[2em] rounded"
            @click="() => form = null"
          >
            <font-awesome-icon icon="arrow-left"/>
          </button>
          <button
            v-else
            class="text-[1.2em] hover:text-secondary-highlighted active:text-secondary-selected
              flex justify-center items-center h-[2em] w-[2em] rounded"
            @click="() => cancel()"
          >
            <font-awesome-icon icon="xmark"/>
          </button>

          <h2 class="text-center my-auto">
            {{ t('jc.component.fileViewer.title') }}
          </h2>

          <!-- filter -->
          <input
            class="text-slate-900 rounded ml-auto"
            :placeholder="t('jc.component.fileViewer.search.placeholder')"
            type="text"
            v-model="query"
            :disabled="form !== null"
          >
        </div>

        <!-- content -->
        <template
          v-if="form == null"
        >
          <keep-alive>
            <div
              class="flex flex-col gap-10"
            >
              <div
                id="list"
                class="grid grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-6 py-2 px-4"
              >
                <!-- actual files -->
                <template
                  v-for="reference in references"
                  :key="reference.id!!"
                >
                  <button
                    class="relative rounded-md overflow-hidden shadow-lg aspect-video bg-neutral-600 group
                      border-4 aria-checked:border-primary hover:border-primary-highlighted active:border-primary-selected"
                    :aria-checked="selected.includes(reference.id!!)"
                    @click="() => select(reference)"
                  >
                    <CharonFile
                      :file-id="reference.id"
                    />
                    <button
                      class="absolute top-3 right-3 z-[80] tablet:invisible group-hover:tablet:visible
                        text-[1rem] bg-primary hover:bg-primary-highlighted active:bg-primary-selected text-white rounded-full inline-flex"
                      @click="() => edit(reference)"
                    >
                      <font-awesome-icon class="aspect-square p-[0.8rem]" icon="pen"/>
                    </button>
                  </button>
                </template>

                <!-- loading placeholder -->
                <template
                  v-if="loading"
                >
                  <template
                    v-for="i in (Math.min(5, (referenceCount || 5) - references.length))"
                    :key="i"
                  >
                    <div class="rounded-md aspect-video animate-pulse bg-secondary-highlighted"/>
                  </template>
                </template>
              </div>

              <button
                class="bg-primary hover:bg-primary-highlighted active:bg-primary-selected
                  disabled:bg-secondary-highlighted disabled:text-gray-400
                  text-white rounded mx-auto px-4 py-2"
                @click="loadNextPage"
                :disabled="loading || !hasNextPage"
              >
                {{ t('jc.component.fileViewer.loadMore.label') }}
              </button>
            </div>
          </keep-alive>
        </template>

        <!-- edit form -->
        <template
          v-else
        >
          <div
            class="flex flex-col gap-5"
          >
            <div
              class="flex flex-col rounded overflow-hidden"
            >
              <CharonFile
                v-show="!form.reference.original.mediaType?.startsWith('image') || !changed"
                :file-id="form.reference.id"
              />
              <canvas
                v-show="form.reference.original.mediaType?.startsWith('image') && changed"
                ref="canvas"
              />
            </div>

            <LocaleSelector
              :locales="locales"
              :selectable="selectableLocales"
              v-model:selected="selectedLocale"
              @click:add="addLocale"
              @click:remove="removeLocale"
            />

            <input
              type="text"
              class="rounded text-black"
              :placeholder="t('jc.component.fileViewer.imageAltText.placeholder')"
              v-model="form!!.reference.locales[selectedLocale].description"
              v-if="selectedLocale"
            >

            <!-- image specific -->
            <template
              v-if="form!!.reference.original.mediaType!!.startsWith('image')"
            >
              <div
                class="flex flex-row gap-10"
              >
                <label class="inline-flex gap-2">
                  <input
                    type="checkbox"
                    v-model="form!!.reference.editData!!.useOverlayText"
                  >
                  <span>
                    {{ t('jc.component.fileViewer.addOverlay.label') }}
                  </span>
                </label>
                <label class="inline-flex gap-2">
                  <input
                    type="checkbox"
                    v-model="form!!.reference.editData!!.useWatermark"
                  >
                  <span>
                    {{ t('jc.component.fileViewer.addWatermark.label') }}
                  </span>
                </label>
              </div>
              <div
                class="flex flex-row gap-2"
              >
                <input
                  type="text"
                  class="rounded text-black w-full"
                  :placeholder="t('jc.component.fileViewer.imageTitle.placeholder')"
                  v-model="form!!.reference.editData!!.overlayText"
                  :disabled="!form!!.reference.editData!!.useOverlayText"
                >
              </div>
            </template>
          </div>
        </template>
      </Container>
    </div>

    <div
      class="fixed right-10 bottom-10 z-[60] flex flex-col gap-5 items-end"
    >
      <button
        class="text-[1.2rem] bg-primary hover:bg-primary-highlighted active:bg-primary-selected text-white rounded-full
          inline-flex max-w-[3.2rem] hover:max-w-[200em] transition-all duration-700 overflow-hidden
          disabled:bg-secondary-highlighted disabled:text-gray-600"
        @click="() => save()"
        :disabled="form == null || saving"
      >
        <font-awesome-icon
          class="aspect-square p-[1rem]"
          :spin="saving"
          :icon="saving ? 'spinner-third' : 'save'"
        />
        <span class="m-auto mr-[1rem]">
          {{ t('jc.component.fileViewer.save.label') }}
        </span>
      </button>
      <label
        class="text-[1.2rem] bg-primary hover:bg-primary-highlighted active:bg-primary-selected text-white rounded-full
          inline-flex max-w-[3.2rem] hover:max-w-[200em] transition-all duration-700 overflow-hidden
          aria-disabled:bg-secondary-highlighted aria-disabled:text-gray-600 hover:cursor-pointer"
        :aria-disabled="form == null || replacing"
      >
        <input
          type="file"
          class="hidden"
          :disabled="form == null || replacing"
          @change="onFileChange"
        >
        <font-awesome-icon
          class="aspect-square p-[1rem]"
          :spin="replacing"
          :icon="replacing ? 'spinner-third' : 'repeat'"
        />
        <span class="m-auto mr-[1rem]">
          {{ t('jc.component.fileViewer.replace.label') }}
        </span>
      </label>
      <button
        class="text-[1.2rem] bg-danger hover:bg-danger-highlighted active:bg-danger-selected text-white rounded-full
          inline-flex max-w-[3.2rem] hover:max-w-[200em] transition-all duration-700 overflow-hidden
          disabled:bg-secondary-highlighted disabled:text-gray-600"
        :disabled="form == null || deleting"
        @click="() => deleteFile()"
      >
        <font-awesome-icon
          class="aspect-square p-[1rem]"
          :spin="deleting"
          :icon="uploading ? 'spinner-third' : 'trash'"
        />
        <span class="m-auto mr-[1rem]">
          {{ t('jc.component.fileViewer.delete.label') }}
        </span>
      </button>
      <span
        class="h-[3px] rounded-l-full rounded-r-full bg-secondary-highlighted w-full"
      />
      <label
        class="text-[1.2rem] bg-primary hover:bg-primary-highlighted active:bg-primary-selected text-white rounded-full
          inline-flex max-w-[3.2rem] hover:max-w-[200em] transition-all duration-700 overflow-hidden
          aria-disabled:bg-secondary-highlighted aria-disabled:text-gray-600 hover:cursor-pointer"
        :aria-disabled="form !== null || uploading"
      >
        <input
          type="file"
          class="hidden"
          :disabled="form !== null || uploading"
          @change="onFileChange"
        >
        <font-awesome-icon
          class="aspect-square p-[1rem]"
          :spin="uploading"
          :icon="uploading ? 'spinner-third' : 'upload'"
        />
        <span class="m-auto mr-[1rem]">
          {{ t('jc.component.fileViewer.upload.label') }}
        </span>
      </label>
      <button
        class="text-[1.2rem] bg-primary hover:bg-primary-highlighted active:bg-primary-selected text-white rounded-full
          inline-flex max-w-[3.2rem] hover:max-w-[200em] transition-all duration-700 overflow-hidden
          disabled:bg-secondary-highlighted disabled:text-gray-600"
        :disabled="form !== null"
        @click="() => confirm()"
      >
        <font-awesome-icon
          class="aspect-square p-[1rem]"
          icon="check"
        />
        <span class="m-auto mr-[1rem]">
          {{ t('jc.component.fileViewer.select.label') }}
        </span>
      </button>
    </div>
  </Overlay>
</template>
